import InputSanitized from 'components/Form/InputSanitized';
import {sanitizeTaxRate} from 'components/Form/InputSanitized/utils';
import Radio from 'components/Form/Radio';
import {ChangeEvent, FC, FocusEvent, useCallback} from 'react';
import {useFormContext} from 'react-hook-form';
import {c, t} from 'ttag';
import {isTaxTypeZero} from 'utils/tax';

import useTaxTypeReadOnly from '../hooks';
import {
    Deserializer,
    ModalFormField,
    NoUnselectedField,
    trimZeroes,
    UNSELECTED,
} from '../utils';

type Props = Record<string, never>;

const RadioTaxRate: FC<Props> = () => {
    const form = useFormContext<ModalFormField>();
    const readOnly = useTaxTypeReadOnly(form);

    const onTaxRateRadioChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            form.clearErrors('taxRateRadio');

            const value = event.target
                .value as NoUnselectedField<'taxRateRadio'>;
            form.setValue('taxRateRadio', value);

            if (value === 'custom') {
                form.setFocus('customTaxRateText');
            } else {
                form.clearErrors('customTaxRateText');
            }
            form.trigger('checkDuplicate');
        },
        [form],
    );

    const onCustomTaxRateFocus = useCallback(() => {
        form.setValue('taxRateRadio', 'custom');
        form.clearErrors('customTaxRateText');
    }, [form]);

    const onCustomTaxRateBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const {value} = event.target;

            if (value === '' || value === UNSELECTED) {
                return;
            }

            const taxRate = trimZeroes(value);
            form.setValue('customTaxRateText', taxRate);
            form.trigger('customTaxRateText');
            form.trigger('checkDuplicate');
        },
        [form],
    );

    return (
        <Radio
            label={c('tax').t`Tax Rate`}
            name="taxRateRadio"
            onChange={onTaxRateRadioChange}
            options={[
                {
                    label: '10%',
                    value: '10_pct',
                },
                {
                    label: '8%',
                    value: '8_pct',
                },
                {
                    label: (
                        <InputSanitized
                            className="text-sm"
                            classNameInput="px-2 h-8 w-24"
                            label="Custom Tax Rate"
                            name="customTaxRateText"
                            onBlurCapture={onCustomTaxRateBlur}
                            onFocus={onCustomTaxRateFocus}
                            placeholder="0"
                            readOnly={readOnly}
                            rules={{
                                validate: (
                                    value: ModalFormField['customTaxRateText'],
                                ) => {
                                    const {taxRateRadio, taxTypeRadio} =
                                        form.getValues();

                                    const disabled =
                                        taxTypeRadio === null
                                            ? false
                                            : isTaxTypeZero(
                                                  Deserializer.taxType(
                                                      taxTypeRadio,
                                                  ),
                                              );

                                    if (disabled || taxRateRadio !== 'custom') {
                                        return undefined;
                                    }

                                    if (value === '' || value === UNSELECTED) {
                                        return t`Input a custom rate.`;
                                    }

                                    if (value === '0') {
                                        if (
                                            taxTypeRadio === 'standard' ||
                                            taxTypeRadio === 'reduced'
                                        ) {
                                            return t`Input non-zero rate for Types Standard and Reduced.`;
                                        }
                                    }

                                    form.trigger('checkDuplicate');

                                    return undefined;
                                },
                            }}
                            sanitizer={sanitizeTaxRate}
                            suffix={<span className="pl-2">%</span>}
                        />
                    ),
                    value: 'custom',
                },
            ]}
            readOnly={readOnly}
            requiredType="hidden"
            rules={{
                validate: (value: ModalFormField['taxRateRadio']) => {
                    if (!value && !readOnly) {
                        return t`Select a Tax Rate.`;
                    }

                    return undefined;
                },
            }}
        />
    );
};

export default RadioTaxRate;
